import { TIMEZONE } from "config/headers";
import { formatInTimeZone } from "date-fns-tz";
import { isoDateFormat } from "utils/dateUtils";

/**
 * A C# DateOnly arrives as UTC midnight, so reading it with local getters shows the previous day in
 * any negative-offset zone — which is also not a value an `<input type="date">` will accept.
 */
export const toDateInputValue = (date?: Date | null): string =>
  date ? formatInTimeZone(date, "UTC", isoDateFormat) : "";

/**
 * Going the other way, the generated client serializes a date with *local* getters, so a DateOnly has
 * to be handed over as local midnight of the intended day. Passing the picker's string straight
 * through would parse as UTC midnight and serialize back one day earlier.
 */
export const toDateOnly = (value?: string): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return undefined;
  }

  return new Date(year, month - 1, day);
};

/** The calendar year of a DateOnly, read in UTC for the same reason. */
export const dateOnlyYear = (date?: Date | null): number | undefined =>
  date ? Number(formatInTimeZone(date, "UTC", "yyyy")) : undefined;

/**
 * Today as yyyy-MM-dd for comparing against a DateOnly, read in the portal's zone. The service decides
 * which set governs against the same Eastern date, and the browser's own zone must not come into it:
 * an admin in Europe would otherwise see a set fall out of force a day before the platform agrees.
 */
export const todayAsDateOnlyString = (): string =>
  formatInTimeZone(new Date(), TIMEZONE!, isoDateFormat);

/** The current year as the business reads it, for naming and dating a new set. */
export const currentBusinessYear = (): number =>
  Number(formatInTimeZone(new Date(), TIMEZONE!, "yyyy"));
